// jobLink — the ONE spelling of "open this job's record".
//
// The owner, 2026-09-10, about the live board: "on finished today jobs, make
// them clickable so we can go directly to the job."
//
// ── Why a module for one string ────────────────────────────────────────────
//
// Because the string is a CONTRACT with two other files, and CLAUDE.md's rule
// about this repo is to declare such a contract once and check it against its
// source rather than hardcode the same rule in a screen:
//
//   apps/AppRouter.js         must have a <Route path="/jobs">
//   apps/shared/navigation.js must have a destination whose path is '/jobs',
//                             or `Allowed` answers "Not part of your app"
//   office/records/OfficePage.js  owns `open` as the record parameter
//
// The third one is the reason this cannot simply live in Now.js. `open` is
// declared as `RECORD_PARAM` in office/records/OfficePage.js, and
// sectionsDoNotCrossImport.test.js refuses a dispatch screen importing from
// office — correctly, since the two sections are meant to be readable apart.
// So the shared thing lives in `shared/`, and jobLink.test.js reads the
// office constant, the route file and the navigation table and asserts all
// three agree with the value below. Change any one of them and a test names
// which.
//
// ── Why the Office record, and not /today/job/:id ──────────────────────────
//
// Both exist and both are reachable. `/today/job/:id` is the CREW screen — the
// checklist, the clock button, the photos — and it answers "what do I do
// here". A dispatcher tapping a finished shift is asking the other question:
// which client, which property, how many hours went on it, who else was on it.
// That is the Office job record, and it is also the one whose list
// (`GET /api/jobs`) returns completed jobs to anybody with job oversight, so a
// shift that finished this morning still opens.

/** The screen that holds job records. Must be a real route AND a destination. */
export const JOB_RECORD_PATH = '/jobs';

/**
 * The query parameter Office reads to decide which record is open. Mirrors
 * `RECORD_PARAM` in apps/office/records/OfficePage.js — mirrored rather than
 * imported because sections may not import each other, and pinned by a test
 * that reads that file so the mirror cannot drift silently.
 */
export const JOB_RECORD_PARAM = 'open';

/**
 * Where to send somebody who wants to see this job, or '' when there is no
 * job to see.
 *
 * The empty answer matters: `finished_today` and `clocked_in` both LEFT JOIN
 * jobs in server/routes/dispatch.js, so `job_id` can be null for a time entry
 * whose job was deleted. Returning '' lets the caller render an untappable row
 * instead of a button that navigates to `/jobs?open=null` — a screen which
 * loads, shows the full list, and silently opens nothing. That is the exact
 * failure OfficePage.js's `recordById` comment describes: "it looks exactly
 * like a list that ignores clicks".
 */
export function jobRecordHref(jobId) {
  if (jobId === null || jobId === undefined || jobId === '') return '';
  const id = String(jobId).trim();
  if (!id || id === 'null' || id === 'undefined') return '';
  return `${JOB_RECORD_PATH}?${JOB_RECORD_PARAM}=${encodeURIComponent(id)}`;
}
